import axios from "axios";
import { t, DEFAULT_LANG, urlLangPrefix } from "@/lib/i18n";

// Backend-Basis-URL — EINZIGE Quelle (nie direkt process.env lesen, sonst entsteht
// bei fehlender Variable still "undefined/api").
// Leer = gleiche Origin: in Produktion (Render Static Site) proxyt ein Rewrite
// /api/* ans Backend. Das ist bewusst so, damit die httpOnly-Auth-Cookies
// First-Party bleiben — als Third-Party-Cookies (getrennte Subdomains) würden sie
// von Safari/Firefox und zunehmend Chrome blockiert und der Login bräche.
// Lokal setzt frontend/.env REACT_APP_BACKEND_URL auf http://localhost:8002.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies (access_token / refresh_token). We do NOT store tokens
// in localStorage: that would be exposed to XSS. `withCredentials: true` lets the
// browser attach the httpOnly cookies to every /api/* request automatically.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Hängt die aktuell gewählte Sprache an jede GET-Anfrage an, damit das Backend
// Inserats-Freitexte (Titel/Beschreibung/Lage) automatisch in dieser Sprache
// ausliefern kann. Endpoints, die `lang` nicht kennen, ignorieren den Parameter.
//
// Der Sprach-Präfix der URL (/de, /el, …) steht VOR localStorage — aus zwei
// Gründen: Erstens gewinnt die URL grundsätzlich (wer einen /de-Link öffnet,
// soll deutsche Inhalte bekommen, egal was ein früherer Besuch gespeichert
// hat). Zweitens ist localStorage beim ALLERERSTEN Aufruf einer /de-URL noch
// leer, wenn die ersten Requests losgehen — Kind-Effekte (React Query) laufen
// vor dem Eltern-Effekt des LanguageProvider, der localStorage erst befüllt.
// Ohne den Präfix-Fallback kämen die ersten Antworten dann auf Englisch.
api.interceptors.request.use((config) => {
  try {
    const method = (config.method || "get").toLowerCase();
    if (method === "get") {
      const lang = urlLangPrefix() || localStorage.getItem("lang");
      if (lang) config.params = { lang, ...(config.params || {}) };
    }
  } catch {
    /* localStorage evtl. nicht verfügbar */
  }
  return config;
});

// ---------------------------------------------------------------------------
// Serverseitig beendete Sitzung erkennen
//
// Seit 29.07.2026 lassen sich Sitzungen widerrufen (token_version, siehe
// backend/auth_utils.py). Damit gibt es einen neuen Zustand: Das Token ist weg,
// die Oberfläche weiß es aber nicht. Ohne das hier sähe ein Nutzer, dessen
// Sitzung auf einem anderen Gerät beendet wurde, eine angemeldet aussehende
// Seite, auf der jeder Klick eine Fehlermeldung wirft — bis er zufällig neu
// lädt. Genau der Eindruck, den „Auf allen Geräten abmelden" nicht machen darf.
//
// Der Handler wird vom AuthProvider gesetzt (hier ist kein React-Kontext).
//
// Ausgenommen sind die Pfade, bei denen ein 401 die NORMALE Antwort ist:
// falsches Passwort am Login, kein Cookie beim ersten /auth/me eines Besuchers.
// Würden die mitzählen, flöge man beim Vertippen aus der Sitzung.
// ---------------------------------------------------------------------------
// Pfade, bei denen ein 401 die NORMALE Antwort ist: falsches Passwort am Login,
// abgelehnte Erneuerung. Würden die mitzählen, flöge man beim Vertippen aus der
// Sitzung — und die Erneuerung riefe sich selbst auf.
//
// `/auth/me` steht hier BEWUSST NICHT drin. Es ist der erste Aufruf jeder Seite;
// wäre es ausgenommen, käme genau dort keine Erneuerung zustande und man wäre
// nach einer Stunde bei jedem Seitenaufruf abgemeldet.
const OHNE_ABMELDUNG = ["/auth/login", "/auth/register", "/auth/refresh",
                        "/auth/app-exchange", "/auth/apple"];
let beiSitzungsende = null;

export function setzeSitzungsendeHandler(fn) {
  beiSitzungsende = fn;
}

// Gab es überhaupt jemals eine Sitzung in diesem Browser?
//
// Die Auth-Cookies sind httpOnly, also für JavaScript unsichtbar — ohne diesen
// Merker müsste bei JEDEM Besucher nach dem 401 auf /auth/me erst eine
// Erneuerung versucht werden, nur um festzustellen, dass er gar nicht
// angemeldet ist. Das wäre eine zusätzliche Anfrage auf jedem Seitenaufruf
// eines anonymen Lesers, also auf der überwiegenden Mehrheit.
//
// Der Merker ist KEIN Sicherheitsmerkmal (er enthält nichts und beweist
// nichts) — er entscheidet nur, ob sich ein Erneuerungsversuch lohnt.
const MERKER = "avliana_sitzung";

export function merkeSitzung(an) {
  try {
    if (an) localStorage.setItem(MERKER, "1");
    else localStorage.removeItem(MERKER);
  } catch {
    /* Private-Mode: dann wird eben immer versucht. */
  }
}

function sitzungMoeglich() {
  try {
    return localStorage.getItem(MERKER) === "1";
  } catch {
    return true;
  }
}

// Stille Erneuerung.
//
// Das Zugangstoken gilt seit 29.07.2026 nur noch eine Stunde statt sieben Tage
// (auth_utils._zugang_minuten). Damit das niemand merkt, wird bei einem 401
// einmal /auth/refresh versucht und die ursprüngliche Anfrage wiederholt. Erst
// wenn auch das scheitert, ist die Sitzung wirklich vorbei.
//
// Einfach-Ausführung: Lädt eine Seite zehn Anfragen parallel und laufen alle
// zehn in den 401, darf daraus GENAU EIN Erneuerungsversuch werden. Sonst
// rotieren zehn Refresh-Aufrufe den Schlüssel gegeneinander weg.
let laufendeErneuerung = null;

function erneuern() {
  if (!laufendeErneuerung) {
    laufendeErneuerung = api.post("/auth/refresh").finally(() => {
      laufendeErneuerung = null;
    });
  }
  return laufendeErneuerung;
}

api.interceptors.response.use(
  (antwort) => antwort,
  async (err) => {
    const cfg = err?.config || {};
    const pfad = cfg.url || "";
    const istRelevant = err?.response?.status === 401 && !OHNE_ABMELDUNG.some((p) => pfad.startsWith(p));
    if (!istRelevant) return Promise.reject(err);

    // `_erneuert` verhindert die Endlosschleife: Wiederholt sich der 401 nach
    // der Erneuerung, ist die Sitzung tatsächlich beendet.
    if (!cfg._erneuert && sitzungMoeglich()) {
      try {
        await erneuern();
        cfg._erneuert = true;
        return api(cfg);
      } catch {
        /* Erneuerung abgelehnt — unten weiter als echtes Sitzungsende. */
      }
    }
    merkeSitzung(false);
    if (beiSitzungsende) beiSitzungsende();
    return Promise.reject(err);
  },
);

// Übersetzen ohne React-Kontext: formatApiError landet an ~100 Stellen direkt in
// toast.error(...), ein Hook wäre dort nicht verfügbar. Die aktive Sprache steht in
// localStorage (dieselbe Quelle wie der Interceptor oben).
function tr(path) {
  try {
    return t(localStorage.getItem("lang") || DEFAULT_LANG, path);
  } catch {
    return t(DEFAULT_LANG, path);
  }
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) {
    // Kein `response` heißt: Die Anfrage kam nie an (Server aus, Netz weg, CORS). axios
    // liefert dafür nur "Network Error" — englisch und nichtssagend, egal welche Sprache
    // eingestellt ist. Der Nutzer soll erkennen, dass es NICHT an seiner Eingabe liegt.
    if (err && !err.response) return tr("common.network_error");
    return tr("common.error");
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  // Strukturierte Fehler ({code, message}, z. B. email_unverified): ohne diesen
  // Zweig würde String(d) dem Nutzer "[object Object]" anzeigen.
  if (typeof d === "object" && d.message) return String(d.message);
  return String(d);
}

export function formatPrice(price, listingType) {
  if (price == null) return "";
  const formatted = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);
  if (listingType === "rent") return `${formatted} / Monat`;
  return formatted;
}
